/*
 * Copyright 2022 Oriflame
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useEffect } from 'react';
import useAsync from 'react-use/lib/useAsync';
import { errorApiRef, useApi } from '@backstage/core-plugin-api';
import { Entity } from '@backstage/catalog-model';
import { scoringDataApiRef } from './ScoringDataApi';

/**
 * Loads all entity scores once. Shared by the board (feature 0013), so the
 * summary strip and the details table render from a SINGLE `getAllScores()`
 * call instead of each fetching its own. Posts any error to the error API.
 */
export const useAllScores = (
  entityKindFilter?: string[],
  entity?: Entity,
) => {
  const errorApi = useApi(errorApiRef);
  const scoringDataApi = useApi(scoringDataApiRef);

  const { error, value, loading } = useAsync(
    async () => scoringDataApi.getAllScores(entityKindFilter, entity),
    [scoringDataApi, entityKindFilter, entity],
  );

  useEffect(() => {
    if (error) {
      errorApi.post(error);
    }
  }, [error, errorApi]);

  return { loading, value, error };
};
